import type { ReactNode } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Text } from '@savvy-kit/foundations/text';
import { ArrowLeft } from 'lucide-react';

import { ADD_ACCOUNT_BACK_LABEL, ADD_ACCOUNT_CANCEL_LABEL } from './add-account-constants';

type AddAccountFormShellProps = {
  readonly title: string;
  readonly onCancel: () => void;
  readonly cancelLabel?: string;
  readonly onStepBack?: () => void;
  readonly onSubmit?: () => void;
  readonly submitLabel?: string;
  readonly submitting?: boolean;
  readonly submitDisabled?: boolean;
  readonly leadingAction?: ReactNode;
  readonly children: ReactNode;
};

export function AddAccountFormShell({
  title,
  onCancel,
  cancelLabel = ADD_ACCOUNT_CANCEL_LABEL,
  onStepBack,
  onSubmit,
  submitLabel,
  submitting = false,
  submitDisabled = false,
  leadingAction,
  children,
}: AddAccountFormShellProps) {
  const body = (
    <>
      <Text variant="h4" className="shrink-0 px-6 pt-6 pb-4">
        {title}
      </Text>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto px-6">{children}</div>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 p-6">
        <div className="flex items-center">{leadingAction}</div>
        <div className="flex flex-1 flex-wrap items-center justify-end gap-3">
          {onStepBack !== undefined && (
            <Button type="button" variant="ghost" onClick={onStepBack}>
              <ArrowLeft size={16} aria-hidden="true" />
              {ADD_ACCOUNT_BACK_LABEL}
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          {submitLabel !== undefined && (
            <Button type="submit" disabled={submitting || submitDisabled}>
              {submitLabel}
            </Button>
          )}
        </div>
      </div>
    </>
  );

  if (onSubmit === undefined) {
    return <div className="flex min-h-0 min-w-0 flex-1 flex-col">{body}</div>;
  }

  return (
    <form
      noValidate
      className="flex min-h-0 min-w-0 flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {body}
    </form>
  );
}
